import React from 'react';
import styled from 'styled-components';
import { breakpoints } from '@/components/responsive';
import { ConnectedStore } from '@/webapi/use-accounts-api';

interface StoreCardDetailsProps {
  storeAlias: string;
  stats?: ConnectedStore;
}

export const StoreCardDetails = ({
  storeAlias,
  stats,
}: StoreCardDetailsProps) => (
  <>
    <span className="store-name">{formatALias(storeAlias)}</span>
    <span>{toStoreDomain(storeAlias)}</span>
    {stats && (
      <>
        <ExperienceCounts store={stats} />
        <StoreStatus store={stats} />
        {stats.isAppEmbedEnabled !== true && (
          <AppEmbedStatus>APP EMBED DISABLED FOR CLIENT</AppEmbedStatus>
        )}
      </>
    )}
  </>
);

const ExperienceCounts = ({ store }: { store: ConnectedStore }) => (
  <ExperiencesWrapper>
    <ExperienceData>
      {store.isUninstalled
        ? `Draft (${store.draftExperiences || 0})`
        : `Live (${store.runningExperiences || 0})`}
    </ExperienceData>
    <ExperienceData>
      {store.isUninstalled
        ? `Paused (${store.pausedExperiences || 0})`
        : `Draft (${store.draftExperiences || 0})`}
    </ExperienceData>
    <ExperienceData>
      {store.isUninstalled
        ? `Archived (${store.archivedExperiences || 0})`
        : `A/B (${store.abTestsExperiences || 0})`}
    </ExperienceData>
  </ExperiencesWrapper>
);

const StoreStatus = ({ store }: { store: ConnectedStore }) => {
  if (store.storeStatus === `PENDING`) {
    return <InactiveStatus>INSTALLATION NOT COMPLETED</InactiveStatus>;
  }
  if (store.isUninstalled) {
    return <InactiveStatus>UNINSTALLED</InactiveStatus>;
  }
  if (store.trialDaysRemaining > 0) {
    const days = store.trialDaysRemaining;
    return (
      <ActiveStatus>
        {days === 1
          ? `1 DAY LEFT IN THE TRIAL`
          : `${days} DAYS LEFT IN THE TRIAL`}
      </ActiveStatus>
    );
  }
  if (store.storeStatus === `ACTIVE`) {
    return <ActiveStatus>SUBSCRIPTION: ACTIVE</ActiveStatus>;
  }
  return null;
};

function toStoreDomain(storeAlias: string): string {
  const domain = `${storeAlias
    ?.replace(`_`, `-`)
    ?.toLocaleLowerCase()}.myshopify.com`;
  const maxLen = 35;
  if (domain.length > maxLen) {
    return `${domain.substring(0, maxLen - 3)}...`;
  }
  return domain;
}

function formatALias(storeAlias: string): string {
  if (storeAlias === `AMACXNL`) {
    return `AMACX COM`;
  }
  if (storeAlias === `AMACX`) {
    return `AMACX NL`;
  }
  if (storeAlias === `DOLLAR_SHAVE_CLUB_STORE`) {
    return `DSC US`;
  }
  const alias = storeAlias?.replaceAll(`_`, ` `)?.substring(0, 20);
  if (alias.length > 20) {
    return `${alias.substring(0, 17)}...`;
  }
  return alias;
}

const ExperiencesWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 2rem;
  justify-content: start;
`;

const ExperienceData = styled.span`
  color: #aeb5be;
  font-family: Inter, serif;
  font-weight: 600;
  font-size: 1.2rem;
`;

const ActiveStatus = styled.span`
  color: #22c55e !important;
  font-weight: 700 !important;
  font-size: 1.2rem !important;

  ${breakpoints.down(`md`)} {
    font-size: 1rem !important;
  }
`;

const InactiveStatus = styled.span`
  color: #6b7280 !important;
  font-weight: 600 !important;
  font-size: 1.2rem !important;

  ${breakpoints.down(`md`)} {
    font-size: 1rem !important;
  }
`;

const AppEmbedStatus = styled.span`
  color: rgb(237, 107, 107) !important;
  font-family: Inter, serif !important;
  font-weight: 700 !important;
  font-size: 1.2rem !important;
  line-height: 3.5rem !important;

  ${breakpoints.down(`md`)} {
    font-size: 1rem !important;
  }
`;
